/**
 * O app na mão, com a tela apagando e outros apps na frente.
 *
 * Antes de qualquer código, o que é possível — porque aqui prometer demais é
 * fácil e desonesto:
 *
 *   Rodar de verdade em segundo plano, recebendo e transmitindo com o app
 *   fechado, um web app NÃO faz. Não é limitação deste projeto: nenhum
 *   navegador dá isso a uma página. O que existe é uma faixa estreita, e é
 *   ela que este módulo ocupa.
 *
 * Android, instalado na tela inicial: com uma sessão de mídia declarada, o
 * sistema entende que há áudio em curso, mostra a notificação de mídia e
 * poupa a página do estrangulamento agressivo de temporizadores. Na prática o
 * áudio continua com a tela apagada ou com outro app na frente. É o caso de
 * uso real — deixar o rádio na escuta no bolso.
 *
 * iOS: o app suspende ao sair da tela. Não há contorno; qualquer coisa que se
 * escreva aqui não muda isso. O que dá para fazer é voltar rápido quando a
 * pessoa retorna, e é o que a reconexão por visibilidade faz.
 *
 * O bloqueio de tela é o outro lado: ele NÃO ajuda em segundo plano — o
 * próprio navegador o solta quando a página some. Ele serve para a tela não
 * apagar enquanto se está operando, que é o que se espera de um rádio ligado
 * na frente da pessoa.
 */

type Estado = {
  callsign: string
  banda: string
  freqKHz: number
}

let wakeLock: WakeLockSentinel | null = null
let travaPedida = false

/**
 * Declara a sessão de mídia ao sistema.
 *
 * É o item que mais pesa para sobreviver em segundo plano no Android, e de
 * quebra dá o que a notificação de mídia mostra: o operador vê em que
 * frequência está sem abrir o app.
 */
export function anunciarSessao(e: Estado): void {
  if (!('mediaSession' in navigator)) return
  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: e.banda,
      artist: e.callsign,
      album: `${(e.freqKHz / 1000).toFixed(3)} MHz`,
      artwork: [
        { src: '/icone-192.png', sizes: '192x192', type: 'image/png' },
        { src: '/icone-512.png', sizes: '512x512', type: 'image/png' },
      ],
    })
    navigator.mediaSession.playbackState = 'playing'
  } catch {
    // Navegador sem MediaMetadata: segue sem, o app não depende disto.
  }
}

export function encerrarSessao(): void {
  if (!('mediaSession' in navigator)) return
  navigator.mediaSession.playbackState = 'none'
  navigator.mediaSession.metadata = null
}

/**
 * Pede para a tela não apagar enquanto se opera.
 *
 * O navegador solta a trava sozinho quando a página some, então isto NÃO é
 * segundo plano — é o oposto: manter a tela viva enquanto a pessoa está ali.
 * Ao voltar, a trava é pedida de novo.
 */
export async function travarTela(): Promise<void> {
  travaPedida = true
  if (!('wakeLock' in navigator) || wakeLock) return
  try {
    wakeLock = await navigator.wakeLock.request('screen')
    wakeLock.addEventListener('release', () => {
      wakeLock = null
    })
  } catch {
    // Recusado (bateria fraca, aba em segundo plano). Não é erro: só não trava.
  }
}

export function soltarTela(): void {
  travaPedida = false
  void wakeLock?.release()
  wakeLock = null
}

/**
 * Liga o app ao ciclo de vida da tela.
 *
 * Duas coisas ao voltar do segundo plano, e as duas importam:
 *
 *  1. Repedir a trava de tela, que o navegador soltou sozinho.
 *  2. Avisar quem cuida da conexão, para reconectar NA HORA. Sem isso, o
 *     socket derrubado durante a suspensão só voltaria no próximo passo do
 *     backoff — e a pessoa encara um app mudo por vários segundos, sem
 *     entender se está no ar ou não.
 */
export function acompanharVisibilidade(aoVoltar: () => void): () => void {
  const mudou = () => {
    if (document.visibilityState !== 'visible') return
    if (travaPedida) void travarTela()
    aoVoltar()
  }
  document.addEventListener('visibilitychange', mudou)
  return () => document.removeEventListener('visibilitychange', mudou)
}
